<template>
  <div style="margin:20px;">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Daily Dispatch</span><br/>
      <span style="font-style: italic; font-size: 18px">Hale Transportation daily dispatch sheet, split by garage{{ garageListLabel }}, with departing (OUT) and already-out (RETURNING) trips.</span>
    </div>

    <div style="position: relative; font-size: 20px; font-weight: bold;" class="align-right d-flex align-items-center justify-content-center">
      <a href="#" class="white-link" v-on:click.prevent="shift_day(-1)">&lt;</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_viewDate" :config="options" :placeholder="details_viewDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_day(1)">&gt;</a>
      &nbsp;&nbsp;|&nbsp;&nbsp;Garage&nbsp;
      <b-form-select style="width: 160px; font-size: 16px;" v-model="garage_filter" :options="garageOptions"/>
      &nbsp;&nbsp;|&nbsp;&nbsp;<b-button v-on:click.prevent="load_data()">Search</b-button>
    </div>

    <div style="position: relative; margin: 10px 0; text-align: center; font-size: 15px;">
      Print range:&nbsp;
      <date-picker style="display:inline-block; width:130px" v-model="print_fromDate" :config="options"/>
      &nbsp;to&nbsp;
      <date-picker style="display:inline-block; width:130px" v-model="print_toDate" :config="options"/>
      &nbsp;&nbsp;<span @click="printPage" style="cursor:pointer" :title="printing ? 'Preparing print...' : 'Print (matches on-screen layout, one sheet per day)'">
        <b-spinner v-if="printing" small style="width: 1rem; height: 1rem;"/>
        <img v-else height="26px" src="/print-icon.png" alt="Print"/>&nbsp;Print
      </span>
      &nbsp;&nbsp;<a href="#" style="font-size: 13px" @click.prevent="downloadCsv" title="Download the print range as a spreadsheet">export CSV</a>
    </div>

    <div v-if="!loading">
      <div v-for="garage in visibleGarages" :key="garage" class="garage-section">
        <div class="garage-header">Garage: {{ garage.toUpperCase() }} ({{ (garageRows[garage] || []).length }})</div>
        <table border="1" class="dispatch-table">
          <tr>
            <th v-for="col in columns" :key="col.key">{{ col.label }}</th>
          </tr>
          <tr v-if="!(garageRows[garage] || []).length">
            <td :colspan="columns.length">No trips</td>
          </tr>
          <tr v-for="(row, idx) in (garageRows[garage] || [])" :key="garage + '-' + idx">
            <td v-for="col in columns" :key="col.key" :class="col.key === 'status' ? statusClass(row.status) : ''">
              {{ row[col.key] }}
            </td>
          </tr>
        </table>
      </div>
    </div>
    <div v-else style="min-height: 400px; display: flex; align-items: center; justify-content: center;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <!-- print stage: renders ONE printPages[] entry at a time, off-screen.
         printPage() below loops through printPages, capturing this single
         page as its own small html2canvas snapshot before moving to the
         next -- every capture stays comfortably within one physical
         letter/landscape page's worth of pixels.

         we used to hand the WHOLE multi-day print range to vue-html2pdf in
         one go, which renders everything into a single giant canvas before
         slicing it into pages. that silently produced an all-blank PDF
         (right page count, zero pixels) once a multi-day range added up to
         enough total height to exceed the browser's max canvas
         width/height -- easy to hit since the print range defaults to the
         rest of the month. capturing page-by-page means the total number
         of days/pages being printed can never blow that limit. -->
    <div
        v-if="printStagePage"
        ref="printStage"
        :style="{ position: 'fixed', left: '-99999px', top: 0, width: printStageWidthPx, background: '#fff', padding: '10px', color: '#000' }"
    >
      <div class="pdf-page-title">Daily Dispatch — {{ printStagePage.date_label }}</div>
      <div v-for="(section, sIdx) in printStagePage.sections" :key="'stage-section-' + sIdx" style="margin-top: 10px;">
        <!-- lighter than the on-screen .garage-header (dark navy bar, fine on a
             monitor) -- a solid dark band prints as a heavy black block and
             eats ink, so print uses a light band with dark text instead. -->
        <div class="garage-header" style="background: #ddd; color: #000;">
          Garage: {{ section.garage.toUpperCase() }} ({{ section.total }}){{ section.continued ? ' — (cont\'d)' : '' }}
        </div>
        <table border="1" class="dispatch-table">
          <tr>
            <th v-for="col in columns" :key="col.key" style="background-color: #F9F8F8;">{{ col.label }}</th>
          </tr>
          <tr v-if="!section.rows.length">
            <td :colspan="columns.length">No trips</td>
          </tr>
          <tr v-for="(row, rIdx) in section.rows" :key="'stage-row-' + rIdx">
            <td v-for="col in columns" :key="col.key" :class="col.key === 'status' ? statusClass(row.status) : ''">
              {{ row[col.key] }}
            </td>
          </tr>
        </table>
      </div>
      <div style="margin-top: 8px; font-size: 11px; text-align: right;">Printed {{ printedAt }}</div>
    </div>
  </div>
</template>
<script>
import { get_tbn_hale_daily_dispatch, api_url } from '../networking/tbn_apis'
import { mapState } from 'vuex'
import { justDate } from '../helpers/dates'
import moment from 'moment'
import axios from 'axios'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

// how many trip rows are allowed on one printed page, PER GARAGE (garages
// never share a page -- each garage-per-day chunk always starts its own
// page). deliberately small: Pickup Location / Destination hold full
// addresses that can wrap to 2-3 lines, and this assumes something close to
// worst case so a page's real content can't realistically exceed one
// physical letter/landscape page. printPage()'s shrink-to-fit is a safety
// net for the rare page that still runs long, not the main mechanism -- if
// this number is too high, most pages end up needing to shrink, which (since
// it shrinks proportionally) leaves visible blank margins on the sides.
const ROWS_PER_PRINT_PAGE = 5

// letter, landscape -> 11in wide. printStage below is rendered at this pixel
// width, so this is also the conversion factor from the print stage's px
// units to the PDF's inches when each page's captured canvas is placed on
// the page (full width, whatever height the content naturally comes out to).
const PRINT_STAGE_WIDTH_PX = 1000
const PDF_PAGE_WIDTH_IN = 11
const PDF_PAGE_HEIGHT_IN = 8.5
const PDF_MARGIN_IN = 0.25

export default {
  name: 'hale_daily_dispatch_report_tbn',

  data() {
    const today = moment()
    return {
      summary: { garages: [] },
      dayRecords: [],
      printRecords: [],
      // the one printPages[] entry currently rendered in the hidden print
      // stage -- see printPage() below.
      printStagePage: null,
      loading: true,
      printing: false,
      details_viewDate: justDate(today),
      print_fromDate: justDate(today),
      print_toDate: justDate(today),
      garage_filter: '',
      // order/labels follow the old RBS "Diary and Scheduling" sheet where possible
      // (Driver, On Duty, PickUp, Spot, Destination, Last Drop, Return Garage); Departure
      // and the destination-arrival time aren't on that sheet but are kept as useful additions.
      columns: [
        { key: 'status', label: 'Status' },
        { key: 'vehicle', label: 'Vehicle' },
        { key: 'vehicle_type', label: 'Type' },
        { key: 'trip_ref', label: 'Trip Ref' },
        { key: 'driver', label: 'Driver' },
        { key: 'driver_start', label: 'Driver Start' },
        { key: 'pickup_location', label: 'Pickup Location' },
        { key: 'spot_time', label: 'Spot Time' },
        { key: 'departure', label: 'Departure' },
        { key: 'destination', label: 'Destination' },
        { key: 'arrival', label: 'Arrival' },
        { key: 'last_drop', label: 'Last Drop' },
        { key: 'return_garage', label: 'Return Garage' },
      ],
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(1, 'year'),
        maxDate: moment().add(1, 'year'),
        keepOpen: false,
        showClear: true,
        showClose: true,
        // without this the widget anchors to the viewport instead of the input
        // (common bootstrap-datetimepicker issue elsewhere in this app)
        widgetPositioning: {
          horizontal: 'left',
          vertical: 'bottom',
        },
      },
    }
  },

  async created() {
    this.load_data()
  },

  methods: {
    statusClass(status) {
      return status === 'OUT' ? 'status-out' : 'status-returning'
    },
    async load_data() {
      this.loading = true
      try {
        const day = moment(this.details_viewDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const { value: { data } } = await get_tbn_hale_daily_dispatch(this.user.tenant, day, day, this.garage_filter)
        this.summary = (data && data.summary) ? data.summary : { garages: [] }
        this.dayRecords = (data && data.results) ? data.results : []
      } catch (e) {
        this.$emit('showAlert', 'Error retrieving daily dispatch data. Try logging in again or contact support.', 'danger', 5000)
      }
      this.loading = false
    },
    shift_day(days) {
      this.details_viewDate = moment(this.details_viewDate, 'MM-DD-YYYY').add(days, 'days').format('MM-DD-YYYY')
      this.load_data()
    },
    // pulls the full print date range (every day in one shot, same shape as the
    // single-day live view) so the PDF below can lay out one page per day.
    async loadPrintRange() {
      const from = moment(this.print_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const to = moment(this.print_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const { value: { data } } = await get_tbn_hale_daily_dispatch(this.user.tenant, from, to, this.garage_filter)
      this.printRecords = (data && data.results) ? data.results : []
    },
    // renders each printPages[] entry into the hidden print stage one at a
    // time and captures it as its own html2canvas snapshot -- see the
    // print-stage template comment for why this has to happen per-page
    // rather than all at once.
    async printPage() {
      if (this.printing)
        return
      this.printing = true
      try {
        await this.loadPrintRange()
        const pages = this.printPages
        if (!pages.length) {
          this.$emit('showAlert', 'No trips found in that date range.', 'warning', 5000)
          return
        }

        const pdf = new jsPDF({ orientation: 'landscape', unit: 'in', format: 'letter' })
        for (let i = 0; i < pages.length; i++) {
          this.printStagePage = pages[i]
          await this.$nextTick()

          const canvas = await html2canvas(this.$refs.printStage, {
            // higher scale = more source pixels per printed inch, so text stays
            // crisp even after a PDF viewer's default fit-to-window zoom
            // downsamples it for display -- 2x was still coming out faint/soft.
            scale: 3,
            useCORS: true,
            backgroundColor: '#ffffff',
          })
          // PNG (lossless), not JPEG -- JPEG's chroma subsampling blurs/lightens
          // thin black text strokes on a white background, which is what was
          // making the printed rows look faint.
          const imgData = canvas.toDataURL('image/png')

          // fit within the page with a margin on all sides, shrinking (never
          // stretching) if needed so a page that runs a bit tall never gets
          // its bottom sliced off by the page edge -- addImage doesn't clip
          // or scale on its own, it just draws at whatever size it's given.
          const availableWidthIn = PDF_PAGE_WIDTH_IN - 2 * PDF_MARGIN_IN
          const availableHeightIn = PDF_PAGE_HEIGHT_IN - 2 * PDF_MARGIN_IN
          const naturalHeightIn = (canvas.height / canvas.width) * availableWidthIn
          const shrink = Math.min(1, availableHeightIn / naturalHeightIn)
          const drawWidthIn = availableWidthIn * shrink
          const drawHeightIn = naturalHeightIn * shrink
          const offsetXIn = PDF_MARGIN_IN + (availableWidthIn - drawWidthIn) / 2

          if (i > 0)
            pdf.addPage('letter', 'landscape')
          pdf.addImage(imgData, 'PNG', offsetXIn, PDF_MARGIN_IN, drawWidthIn, drawHeightIn)
        }

        pdf.save(`${this.printFilename}.pdf`)
      } catch (e) {
        this.$emit('showAlert', 'Error preparing print. Try logging in again or contact support.', 'danger', 5000)
      } finally {
        this.printStagePage = null
        this.printing = false
      }
    },
    async downloadCsv() {
      this.$emit('showAlert', 'preparing download...', 'info', 3000)
      const from = moment(this.print_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const to = moment(this.print_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const filename = `hale-daily-dispatch-${from}-to-${to}.csv`
      try {
        const response = await axios.post(
          `${api_url}tbn/hale/daily-dispatch/print`,
          {
            tenant: this.user.tenant,
            fromDate: from,
            toDate: to,
            garage: this.garage_filter || '',
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'text/csv',
            },
            responseType: 'text',
          }
        )
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(url)
        link.remove()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      } catch (e) {
        this.$emit('showAlert', 'Download failed.', 'danger')
      }
    },
  },

  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    printedAt() {
      return moment().format('MMMM Do YYYY, h:mm a')
    },
    printFilename() {
      const from = moment(this.print_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const to = moment(this.print_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      return `hale-daily-dispatch-${from}-to-${to}`
    },
    printStageWidthPx() {
      return `${PRINT_STAGE_WIDTH_PX}px`
    },
    garageOptions() {
      const opts = [{ value: '', text: 'All Garages' }]
      for (const g of (this.summary.garages || [])) {
        opts.push({ value: g, text: g })
      }
      return opts
    },
    // driven by summary.garages (from the backend's GARAGES map) rather than hardcoded
    // names, so this stays accurate if Hale adds/renames a garage.
    garageListLabel() {
      const garages = this.summary.garages || []
      return garages.length ? ` (${garages.join(' / ')})` : ''
    },
    visibleGarages() {
      const all = this.summary.garages || []
      return this.garage_filter ? all.filter(g => g === this.garage_filter) : all
    },
    garageRows() {
      const day = this.dayRecords[0]
      return (day && day.garages) ? day.garages : {}
    },
    // one entry per printed page: { date_label, sections: [{ garage, rows, total, continued }] }.
    // packs sections (across garages) onto a page up to ROWS_PER_PRINT_PAGE
    // rows total, only starting a new page once the current one is full --
    // a light garage (few trips) shares the page with the next garage's
    // rows instead of getting its own mostly-empty page. a garage with more
    // rows than fit in the remaining budget just splits across pages, same
    // as before, marked "(cont'd)" on its later chunks.
    printPages() {
      const garages = this.visibleGarages
      const pages = []
      for (const day of this.printRecords) {
        let page = { date_label: day.date_label, sections: [] }
        let used = 0

        for (const garage of garages) {
          const rows = (day.garages && day.garages[garage]) || []

          if (!rows.length) {
            if (used >= ROWS_PER_PRINT_PAGE) {
              pages.push(page)
              page = { date_label: day.date_label, sections: [] }
              used = 0
            }
            page.sections.push({ garage, rows: [], total: 0, continued: false })
            continue
          }

          let idx = 0
          let firstChunk = true
          while (idx < rows.length) {
            if (used >= ROWS_PER_PRINT_PAGE) {
              pages.push(page)
              page = { date_label: day.date_label, sections: [] }
              used = 0
            }
            const take = Math.min(ROWS_PER_PRINT_PAGE - used, rows.length - idx)
            const chunk = rows.slice(idx, idx + take)
            page.sections.push({ garage, rows: chunk, total: rows.length, continued: !firstChunk })
            used += chunk.length
            idx += chunk.length
            firstChunk = false
          }
        }

        pages.push(page)
      }
      return pages
    },
  },

  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      }
    },
  },
}
</script>

<style scoped>
.white-link { font-size: 22px; font-weight: bold; color: white !important; }
.garage-section { margin-bottom: 20px; }
.garage-header {
  background: #333;
  color: #fff;
  font-size: 15px;
  font-weight: bold;
  padding: 6px 10px;
  margin-bottom: 4px;
  text-align: center;
}
.pdf-page-title {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
}
.dispatch-table {
  border-collapse: collapse;
  font-size: 13px;
  width: 100%;
}
.dispatch-table th,
.dispatch-table td {
  border: 1px solid #999;
  padding: 6px 8px;
  text-align: left;
}
.dispatch-table th {
  background-color: #F9F8F8;
  font-weight: bold;
}
.status-out { color: #0a6b0a; font-weight: bold; }
.status-returning { color: #a35400; font-weight: bold; }
</style>
